import { personalProjectsApi } from './personalProjectsApi.ts';

interface ProjectTask {
  _id: string;
  title: string;
  description: string;
  status: 'Todo' | 'In Progress' | 'Completed' | 'Blocked' | 'Review';
  priority: 'Low' | 'Medium' | 'High';
  category: string;
  estimatedTime: string;
  sourceProjectId?: string;
  sourceIdeaId?: string;
  createdAt: string;
  updatedAt: string;
}

interface PersonalProject {
  _id: string;
  title: string;
  description: string;
  category: string;
  status: 'Planning' | 'Active' | 'Completed' | 'On Hold';
  progress: number;
  sourceIdeaId?: string;
  createdAt: string;
  updatedAt: string;
}

interface ProjectWithTasks extends PersonalProject {
  tasks: ProjectTask[];
}

interface TaskCreationData {
  title: string;
  description: string;
  priority: 'Low' | 'Medium' | 'High';
  category: string;
  estimatedTime: string;
  sourceIdeaId?: string;
  dueDate?: string;
}

const API_BASE_URL = () => {
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_API_URL || 'https://findashback.onrender.com';
  }
  return process.env.REACT_APP_API_URL || 'http://localhost:5000';
};

function getAuthHeaders() {
  const token = localStorage.getItem('authToken') || localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  };
}


// Get projects with their associated tasks
const getProjectsWithTasks = async (): Promise<ProjectWithTasks[]> => {
  try {
    // Get all projects (with tasks included)
    const projects = await personalProjectsApi.getProjects();
    // If backend already returns tasks, use them directly
    return projects as ProjectWithTasks[];
  } catch (error) {
    console.error('Error fetching projects with tasks:', error);
    throw error;
  }
};

// Get tasks for a specific project
const getProjectTasks = async (projectId: string): Promise<ProjectTask[]> => {
  try {
    const response = await fetch(`${API_BASE_URL()}/api/tasks?sourceProjectId=${projectId}`, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.tasks || [];
  } catch (error) {
    console.error('Error fetching project tasks:', error);
    return [];
  }
};

// Create a new task for a project
const createProjectTask = async (projectId: string, taskData: TaskCreationData, projectDueDate?: string): Promise<ProjectTask> => {
  try {
    const response = await fetch(`${API_BASE_URL()}/api/tasks`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify({
        ...taskData,
        sourceProjectId: projectId,
        status: 'Todo',
        dueDate: projectDueDate || taskData.dueDate || ''
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.task;
  } catch (error) {
    console.error('Error creating project task:', error);
    throw error;
  }
};

// Update task status
const updateTaskStatus = async (taskId: string, status: ProjectTask['status']): Promise<ProjectTask> => {
  try {
    const response = await fetch(`${API_BASE_URL()}/api/tasks/${taskId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify({ status }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.task;
  } catch (error) {
    console.error('Error updating task status:', error);
    throw error;
  }
};

// Delete a task
const deleteTask = async (taskId: string): Promise<void> => {
  try {
    const response = await fetch(`${API_BASE_URL()}/api/tasks/${taskId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  } catch (error) {
    console.error('Error deleting task:', error);
    throw error;
  }
};

// Sync project progress (trigger backend calculation)
const syncProjectProgress = async (projectId: string): Promise<void> => {
  try {
    const response = await fetch(`${API_BASE_URL()}/api/projects/${projectId}/sync-progress`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  } catch (error) {
    console.error('Error syncing project progress:', error);
    throw error;
  }
};

// Get task status information
const getTaskStatusInfo = (status: string) => {
  const statusInfo = {
    'Todo': { color: 'gray', bgColor: 'bg-gray-100', textColor: 'text-gray-700' },
    'In Progress': { color: 'blue', bgColor: 'bg-blue-100', textColor: 'text-blue-700' },
    'Completed': { color: 'green', bgColor: 'bg-green-100', textColor: 'text-green-700' },
    'Blocked': { color: 'red', bgColor: 'bg-red-100', textColor: 'text-red-700' },
    'Review': { color: 'yellow', bgColor: 'bg-yellow-100', textColor: 'text-yellow-700' }
  };

  return statusInfo[status as keyof typeof statusInfo] || statusInfo['Todo'];
};

export const projectTaskService = {
  getProjectsWithTasks,
  getProjectTasks,
  createProjectTask,
  updateTaskStatus,
  deleteTask,
  syncProjectProgress,
  getTaskStatusInfo
};

export type { ProjectTask, ProjectWithTasks, TaskCreationData };
